import type { LearningPath, Unit, Lesson, Question } from './lessons';

export type { LearningPath, Unit, Lesson };

export const basketballLearningPaths: LearningPath[] = [
  {
    id: 'basketball-basics',
    difficulty: 'beginner',
    title: 'The Basics',
    description: 'Learn positions, rules, and how the game of basketball works.',
    color: 'primary',
    units: [
      {
        id: 'bball-positions-101',
        title: 'Positions 101',
        icon: '🏀',
        requiredXp: 0,
        lessons: [
          {
            id: 'bball-five-positions',
            title: 'The Five Positions',
            description: 'Learn each position on the basketball court.',
            conceptText: [
              'Basketball has five positions: Point Guard (PG), Shooting Guard (SG), Small Forward (SF), Power Forward (PF), and Center (C). Each has a distinct role.',
              'The Point Guard is the floor general — they bring the ball up court, call plays, and distribute to teammates. Think of them as the quarterback of basketball.',
              'The Shooting Guard is typically the best perimeter shooter. The Small Forward is the most versatile player. The Power Forward plays near the basket with physicality, and the Center anchors the paint.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bb-tf1',
                type: 'true-false',
                question: 'A player can take two steps after picking up their dribble without it being called traveling.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True! A player is allowed a "gather step" plus two steps after picking up the ball, which is why euro-steps and layups are legal.'
              },
              {
                id: 'bb-pos-q1',
                type: 'diagram-identify',
                question: 'Study the positions on the court. Which position is often called the "floor general"?',
                diagram: {
                  title: 'Basketball Positions',
                  sport: 'basketball',
                  players: [
                    { x: 50, y: 68, type: 'offense', label: 'PG' },
                    { x: 78, y: 58, type: 'offense', label: 'SG' },
                    { x: 22, y: 50, type: 'offense', label: 'SF' },
                    { x: 35, y: 30, type: 'offense', label: 'PF' },
                    { x: 50, y: 20, type: 'offense', label: 'C' },
                  ],
                  routes: []
                },
                options: ['Center', 'Point Guard', 'Power Forward', 'Shooting Guard'],
                correctIndex: 1,
                explanation: 'The Point Guard (PG) runs the offense, calls plays, and distributes the ball — earning them the nickname "floor general."',
              },
              {
                id: 'bb-scenario1',
                type: 'scenario',
                question: 'A player drives to the basket. A defender is standing still inside the restricted area with both feet set. Contact occurs. What\'s the call?',
                options: ['Blocking foul on the defender', 'Charge on the offensive player', 'No call — play on'],
                correctIndex: 0,
                explanation: 'If the defender is inside the restricted area (the small arc under the basket), they cannot draw a charge — it\'s a blocking foul on the defender.'
              },
              {
                id: 'bb-pos-q2',
                type: 'multiple-choice',
                question: 'Which position typically anchors the paint and plays closest to the basket?',
                options: ['Small Forward', 'Shooting Guard', 'Center', 'Point Guard'],
                correctIndex: 2,
                explanation: 'The Center plays closest to the basket, focusing on rebounding, rim protection, and scoring inside.',
              },
              {
                id: 'bb-tf2',
                type: 'true-false',
                question: 'In the NBA, a team has 30 seconds to attempt a shot.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! The NBA shot clock is 24 seconds, not 30. The 30-second shot clock is used in NCAA women\'s basketball.'
              },
              {
                id: 'bb-pos-q3',
                type: 'fix-mistake',
                question: '"The Shooting Guard\'s primary job is to set screens and rebound." What\'s wrong?',
                options: [
                  'Nothing — that\'s exactly what shooting guards do',
                  'The Shooting Guard is primarily a perimeter scorer and shooter, not a screener/rebounder',
                  'The Shooting Guard only plays defense',
                  'The Shooting Guard always brings the ball up court',
                ],
                correctIndex: 1,
                explanation: 'Shooting Guards are known for perimeter shooting, scoring off screens, and creating their own shot. Screening and rebounding are more associated with bigs (PF/C).',
              },
            ],
          },
          {
            id: 'bball-court-layout',
            title: 'The Court',
            description: 'Understand the basketball court and its key areas.',
            conceptText: [
              'A basketball court is 94 feet long and 50 feet wide. The key areas include the paint (restricted area near the basket), the three-point line, the free-throw line, and half court.',
              'The paint (or "the key") is the rectangular area under the basket. Offensive players can\'t stand in the paint for more than 3 seconds — this is called a 3-second violation.',
              'The three-point line is an arc around the basket. Shots made from beyond this line are worth 3 points instead of 2. The corner three is the shortest three-point shot on the court.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bb-court-q1',
                type: 'diagram-identify',
                question: 'Study the court diagram. The shaded area near the basket is "the paint." How long can an offensive player stand there?',
                diagram: {
                  title: 'Basketball Court Areas',
                  sport: 'basketball',
                  players: [
                    { x: 50, y: 14, type: 'offense', label: 'Paint' },
                    { x: 50, y: 19, type: 'offense', label: 'FT' },
                    { x: 22, y: 9, type: 'offense', label: '3PT' },
                    { x: 78, y: 9, type: 'offense', label: '3PT' },
                  ],
                  routes: []
                },
                options: ['5 seconds', '3 seconds', '10 seconds', 'No limit'],
                correctIndex: 1,
                explanation: 'An offensive player can only remain in the paint for 3 consecutive seconds. This keeps players from camping under the basket.',
              },
              {
                id: 'bb-court-q2',
                type: 'multiple-choice',
                question: 'Which three-point shot is the shortest distance from the basket?',
                options: ['Top of the key', 'Wing three', 'Corner three', 'They\'re all the same distance'],
                correctIndex: 2,
                explanation: 'The corner three is approximately 22 feet from the basket, while the rest of the three-point arc is 23 feet 9 inches (NBA). This makes corners a high-value shot.',
              },
              {
                id: 'bb-court-q3',
                type: 'multiple-choice',
                question: 'How many points is a shot worth if made from beyond the three-point line?',
                options: ['1 point', '2 points', '3 points', '4 points'],
                correctIndex: 2,
                explanation: 'Shots from beyond the arc are worth 3 points, making them a key part of modern basketball strategy.',
              },
            ],
          },
          {
            id: 'bball-basic-rules',
            title: 'Basic Rules',
            description: 'The fundamental rules every fan and player should know.',
            conceptText: [
              'Basketball is played in four quarters (12 minutes in the NBA, 10 in FIBA). The team with the most points at the end wins. If tied, overtime periods are played.',
              'Key violations: Traveling (moving without dribbling), Double Dribble (stopping your dribble then dribbling again), and Shot Clock violation (failing to attempt a shot within 24 seconds in the NBA).',
              'Fouls are physical contact penalties. A player who commits 6 fouls in the NBA (5 in college) "fouls out" and must leave the game. The opposing team gets free throws on shooting fouls.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bb-rules-q1',
                type: 'multiple-choice',
                question: 'In the NBA, how many seconds does a team have to attempt a shot?',
                options: ['30 seconds', '35 seconds', '24 seconds', '20 seconds'],
                correctIndex: 2,
                explanation: 'The NBA shot clock is 24 seconds. This keeps the pace of play fast and prevents teams from stalling.',
              },
              {
                id: 'bb-rules-q2',
                type: 'multiple-choice',
                question: 'How many personal fouls cause a player to foul out in the NBA?',
                options: ['4', '5', '6', '7'],
                correctIndex: 2,
                explanation: 'In the NBA, a player fouls out after 6 personal fouls. In college and FIBA, the limit is 5.',
              },
              {
                id: 'bb-rules-q3',
                type: 'fix-mistake',
                question: '"Traveling is when you dribble the ball with two hands at the same time." What\'s wrong?',
                options: [
                  'Nothing, that\'s correct',
                  'That describes a double dribble — traveling is moving without dribbling',
                  'Traveling is a defensive violation',
                  'You can dribble with two hands in basketball',
                ],
                correctIndex: 1,
                explanation: 'Traveling is taking steps without dribbling the ball. Dribbling with both hands simultaneously is a double dribble violation — a separate rule.',
              },
            ],
          },
        ],
      },
      {
        id: 'bball-offense-basics',
        title: 'Offensive Fundamentals',
        icon: '🎯',
        requiredXp: 40,
        lessons: [
          {
            id: 'bball-pick-and-roll',
            title: 'Pick & Roll',
            description: 'The most fundamental play in basketball.',
            conceptText: [
              'The Pick & Roll (also called a Ball Screen) is the most common play in basketball. A "big" (PF or C) sets a screen on the ball handler\'s defender, then "rolls" to the basket.',
              'The ball handler uses the screen to create separation. They can: drive to the basket, pull up for a mid-range shot, pass to the rolling screener, or kick out to an open shooter.',
              'Defenses counter the pick & roll with different coverages: hedging (the screener\'s defender jumps out), switching (defenders swap assignments), dropping (the big stays near the paint), or trapping (both defenders attack the ball).',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bb-pnr-q1',
                type: 'diagram-identify',
                question: 'Study the pick & roll diagram. After the screener (C) sets the screen, what does he do?',
                diagram: {
                  title: 'Pick & Roll',
                  sport: 'basketball',
                  players: [
                    { x: 50, y: 60, type: 'offense', label: 'PG' },
                    { x: 48, y: 52, type: 'offense', label: 'C' },
                    { x: 22, y: 14, type: 'offense', label: 'SG' },
                    { x: 78, y: 14, type: 'offense', label: 'SF' },
                    { x: 85, y: 55, type: 'offense', label: 'PF' },
                    { x: 52, y: 58, type: 'defense', label: '' },
                    { x: 46, y: 48, type: 'defense', label: '' },
                  ],
                  routes: [
                    { fromX: 50, fromY: 60, toX: 38, toY: 50, type: 'solid' },
                    { fromX: 48, fromY: 52, toX: 50, toY: 30, type: 'dashed' },
                  ]
                },
                options: [
                  'Stays in place and waits',
                  'Rolls toward the basket looking for a pass',
                  'Runs to the three-point line',
                  'Goes back to half court',
                ],
                correctIndex: 1,
                explanation: 'After setting the screen, the screener "rolls" (turns and moves) toward the basket (shown by the dashed arrow), creating a scoring opportunity if the defense collapses on the ball handler.',
              },
              {
                id: 'bb-pnr-q2',
                type: 'multiple-choice',
                question: 'When the defense "switches" on a pick & roll, what happens?',
                options: [
                  'Both defenders trap the ball handler',
                  'The screener\'s defender drops to the paint',
                  'The defenders swap their assignments — each takes the other\'s man',
                  'The defense calls a timeout',
                ],
                correctIndex: 2,
                explanation: 'A switch means the defenders exchange assignments. The screener\'s defender now guards the ball handler, and vice versa. This can create size mismatches.',
              },
              {
                id: 'bb-pnr-q3',
                type: 'multiple-choice',
                question: 'Why is the Pick & Roll so effective?',
                options: [
                  'It only works against zone defense',
                  'It forces the defense into a 2-on-1 situation, creating multiple scoring options',
                  'It eliminates the shot clock',
                  'It prevents the other team from substituting',
                ],
                correctIndex: 1,
                explanation: 'The Pick & Roll creates a 2-on-1 advantage. The ball handler and screener work together while the defense must make difficult decisions, opening up driving lanes, rolls, and kick-outs.',
              },
            ],
          },
          {
            id: 'bball-spacing',
            title: 'Floor Spacing',
            description: 'Why spacing wins games in modern basketball.',
            conceptText: [
              'Floor spacing is the foundation of modern basketball offense. When players spread out, it creates driving lanes and makes it harder for the defense to help.',
              'The modern "5-out" spacing puts a shooter at each of the five spots around the three-point line. This stretches the defense and gives the ball handler room to operate.',
              'Poor spacing (clustering) allows the defense to guard multiple players at once. Good spacing forces defenders into difficult help-and-recover situations, creating open shots.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bb-space-q1',
                type: 'diagram-identify',
                question: 'Study the spacing diagram. This is "5-out" spacing. What does it mean?',
                diagram: {
                  title: '5-Out Spacing',
                  sport: 'basketball',
                  players: [
                    { x: 50, y: 60, type: 'offense', label: '1' },
                    { x: 22, y: 48, type: 'offense', label: '2' },
                    { x: 78, y: 48, type: 'offense', label: '3' },
                    { x: 18, y: 14, type: 'offense', label: '4' },
                    { x: 82, y: 14, type: 'offense', label: '5' },
                  ],
                  routes: []
                },
                options: [
                  'Five players standing out of bounds',
                  'All five players spread around the three-point line',
                  'Five players in the paint',
                  'Only using 5 seconds of shot clock',
                ],
                correctIndex: 1,
                explanation: '"5-out" means all five offensive players position themselves around the perimeter (three-point line), maximizing driving lanes and creating space for the ball handler.',
              },
              {
                id: 'bb-space-q2',
                type: 'fix-mistake',
                question: '"Good spacing means all players should stand as close together as possible to pass quickly." What\'s wrong?',
                options: [
                  'Nothing — tight spacing helps passing',
                  'Good spacing means spreading out to create driving lanes and force defensive help rotations',
                  'Spacing only matters on defense',
                  'Spacing is irrelevant in modern basketball',
                ],
                correctIndex: 1,
                explanation: 'Clustering players together lets the defense cover multiple threats easily. Spreading out forces one-on-one matchups and creates driving lanes that are hard to help on.',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'basketball-tactics',
    difficulty: 'intermediate',
    title: 'Tactical Concepts',
    description: 'Defensive schemes, offensive sets, and advanced strategy.',
    color: 'accent',
    units: [
      {
        id: 'bball-defense-schemes',
        title: 'Defensive Schemes',
        icon: '🛡️',
        requiredXp: 0,
        lessons: [
          {
            id: 'bball-man-vs-zone',
            title: 'Man vs. Zone Defense',
            description: 'The two core defensive philosophies.',
            conceptText: [
              'In man-to-man defense, each defender is assigned a specific offensive player to guard. You follow your man everywhere on the court.',
              'In zone defense, each defender guards an area (zone) of the court rather than a specific player. Common zones include the 2-3 zone, 3-2 zone, and 1-3-1 zone.',
              'Man defense is better against teams with weak individual players. Zone defense is better against poor shooting teams and protects the paint. Most teams mix both depending on the situation.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bb-def-q1',
                type: 'diagram-identify',
                question: 'Study the 2-3 zone. How many defenders guard the area near the basket (bottom row)?',
                diagram: {
                  title: '2-3 Zone Defense',
                  sport: 'basketball',
                  players: [
                    { x: 38, y: 52, type: 'defense', label: '' },
                    { x: 62, y: 52, type: 'defense', label: '' },
                    { x: 25, y: 30, type: 'defense', label: '' },
                    { x: 50, y: 25, type: 'defense', label: '' },
                    { x: 75, y: 30, type: 'defense', label: '' },
                  ],
                  routes: []
                },
                options: ['2', '3', '4', '1'],
                correctIndex: 1,
                explanation: 'In a 2-3 zone, the "3" represents three defenders along the baseline/paint area. Two defenders guard the perimeter up top. This protects the paint but can be vulnerable to three-point shooting.',
              },
              {
                id: 'bb-def-q2',
                type: 'multiple-choice',
                question: 'What is the main weakness of a zone defense?',
                options: [
                  'It\'s too tiring for players',
                  'It can be exploited by good three-point shooting and ball movement',
                  'It fouls too much',
                  'The coach gets ejected',
                ],
                correctIndex: 1,
                explanation: 'Zone defense packs the paint but leaves gaps on the perimeter. Teams that shoot well from three and move the ball quickly to find open shooters can exploit zone coverage.',
              },
              {
                id: 'bb-def-q3',
                type: 'fix-mistake',
                question: '"In man-to-man defense, you guard a section of the court, not a specific player." What\'s wrong?',
                options: [
                  'Nothing, that\'s correct',
                  'That describes zone defense — man-to-man means guarding a specific assigned player',
                  'Man-to-man defense doesn\'t exist in basketball',
                  'You guard the ball in man-to-man',
                ],
                correctIndex: 1,
                explanation: 'Man-to-man defense assigns each defender to guard a specific offensive player. Zone defense is where you guard an area. These are opposite concepts.',
              },
            ],
          },
          {
            id: 'bball-fast-break',
            title: 'The Fast Break',
            description: 'Scoring in transition before the defense sets up.',
            conceptText: [
              'A fast break (or transition offense) happens when a team pushes the ball up court quickly after gaining possession — before the defense can set up.',
              'The ideal fast break creates a numbers advantage: 2-on-1, 3-on-2, or even a breakaway. The key is speed, passing ahead, and attacking before the defense recovers.',
              'Teams that excel in transition (like the "Seven Seconds or Less" Suns or modern pace-and-space teams) force opponents to choose between running back on defense or crashing the offensive glass.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bb-fb-q1',
                type: 'diagram-identify',
                question: 'This shows a 2-on-1 fast break. Why is this situation effective for the offense?',
                diagram: {
                  title: '2-on-1 Fast Break',
                  sport: 'basketball',
                  players: [
                    { x: 40, y: 45, type: 'offense', label: '1' },
                    { x: 65, y: 38, type: 'offense', label: '2' },
                    { x: 50, y: 25, type: 'defense', label: '' },
                  ],
                  routes: [
                    { fromX: 40, fromY: 45, toX: 42, toY: 28, type: 'solid' },
                    { fromX: 65, fromY: 38, toX: 65, toY: 20, type: 'dashed' },
                  ]
                },
                options: [
                  'It lets you play with fewer players',
                  'It creates a numbers advantage before the defense can set up',
                  'The shot clock doesn\'t apply in transition',
                  'Referees give bonus points for fast breaks',
                ],
                correctIndex: 1,
                explanation: 'Fast breaks are effective because they create outnumbered situations (e.g., 2-on-1). The lone defender can\'t guard both attackers, so the offense gets easier scoring opportunities.',
              },
              {
                id: 'bb-fb-q2',
                type: 'multiple-choice',
                question: 'In a 2-on-1 fast break, what should the ball handler do?',
                options: [
                  'Always shoot immediately',
                  'Always pass to the teammate',
                  'Attack the defender — if they commit, pass; if they sag, score',
                  'Stop and wait for teammates to catch up',
                ],
                correctIndex: 2,
                explanation: 'In a 2-on-1, the ball handler should attack the lone defender and read their reaction. If the defender commits to the ball, pass. If they hang back, take it to the basket.',
              },
            ],
          },
        ],
      },
      {
        id: 'bball-advanced-offense',
        title: 'Offensive Systems',
        icon: '📋',
        requiredXp: 80,
        lessons: [
          {
            id: 'bball-motion-offense',
            title: 'Motion Offense',
            description: 'Continuous movement and reads without set plays.',
            conceptText: [
              'Motion offense is a free-flowing system based on reads, screens, and player movement rather than scripted plays. Players follow rules and principles instead of memorized patterns.',
              'Key principles: cut when your defender looks away (backdoor cut), set screens away from the ball, space the floor, and make the extra pass for a better shot.',
              'The Warriors\' dynasty was built on motion offense principles — constant ball and player movement, devastating off-ball screens, and unselfish passing to find the best shot.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bb-motion-q1',
                type: 'multiple-choice',
                question: 'What makes motion offense different from set plays?',
                options: [
                  'Motion offense has no rules at all',
                  'Players follow principles and react to the defense rather than running scripted patterns',
                  'Motion offense only uses the pick & roll',
                  'Players don\'t move in motion offense',
                ],
                correctIndex: 1,
                explanation: 'Motion offense is based on principles (cut, screen, space) and reads rather than step-by-step scripted plays. Players react to what the defense gives them.',
              },
              {
                id: 'bb-motion-q2',
                type: 'multiple-choice',
                question: 'When should an off-ball player make a backdoor cut?',
                options: [
                  'At the start of every possession',
                  'When their defender turns their head or overplays the passing lane',
                  'Only in the fourth quarter',
                  'Never — backdoor cuts aren\'t part of motion offense',
                ],
                correctIndex: 1,
                explanation: 'A backdoor cut is most effective when the defender loses sight of their man or denies the pass too aggressively. The cutter exploits the overplay by going behind the defender to the basket.',
              },
            ],
          },
          {
            id: 'bball-horns-sets',
            title: 'Horns & Elbow Sets',
            description: 'Popular half-court offensive sets in the NBA.',
            conceptText: [
              '"Horns" is an offensive set where two bigs start at the elbows (free-throw line extended) and three players are on the perimeter. It\'s one of the most popular sets in the NBA.',
              'From Horns, teams can run pick & rolls on either side, dribble handoffs, pin-down screens, or quick hitters. The versatility makes it difficult for defenses to scout and prepare.',
              'Elbow sets position a big at the free-throw line (the "elbow") as a passer. From here, wings can cut, screens can be set, and the big can face up for mid-range shots or drive.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bb-horns-q1',
                type: 'diagram-identify',
                question: 'Study the Horns set. Where do the two bigs (PF and C) start?',
                diagram: {
                  title: 'Horns Set',
                  sport: 'basketball',
                  players: [
                    { x: 50, y: 65, type: 'offense', label: 'PG' },
                    { x: 38, y: 43, type: 'offense', label: 'PF' },
                    { x: 62, y: 43, type: 'offense', label: 'C' },
                    { x: 15, y: 30, type: 'offense', label: 'SG' },
                    { x: 85, y: 30, type: 'offense', label: 'SF' },
                  ],
                  routes: []
                },
                options: [
                  'Under the basket',
                  'In the corners',
                  'At the elbows (free-throw line extended)',
                  'At half court',
                ],
                correctIndex: 2,
                explanation: 'In Horns, both bigs position themselves at the elbows — one on each side of the free-throw line. This creates options for ball screens in either direction.',
              },
              {
                id: 'bb-horns-q2',
                type: 'multiple-choice',
                question: 'Why is the Horns set so popular in the NBA?',
                options: [
                  'It\'s the easiest play to remember',
                  'Its versatility — it can flow into pick & rolls, handoffs, or cuts in either direction',
                  'It guarantees a three-pointer',
                  'Only point guards can run it',
                ],
                correctIndex: 1,
                explanation: 'Horns is popular because of its flexibility. From the same starting alignment, teams can run many different actions, making it unpredictable for the defense.',
              },
            ],
          },
        ],
      },
    ],
  },
  // ── New: Game Strategy (Intermediate) ────────────────────
  {
    id: 'basketball-strategy',
    difficulty: 'intermediate' as const,
    title: 'Game Strategy',
    description: 'Late-game situations, foul strategy, and tactical adjustments.',
    color: 'primary',
    units: [
      {
        id: 'bball-late-game',
        title: 'Crunch Time',
        icon: '⏰',
        requiredXp: 150,
        lessons: [
          {
            id: 'bball-clock-fouls',
            title: 'Clock & Foul Strategy',
            description: 'How smart teams manage the final minutes.',
            conceptText: [
              'In the final two minutes, the game changes completely. Trailing teams intentionally foul to stop the clock and get the ball back — it\'s called "hack-a-strategy" or simply fouling to extend the game.',
              'Leading teams want to run the shot clock down and take high-percentage shots. Every possession matters — a turnover can swing a 5-point game into a 2-point game in seconds.',
              'The bonus and double bonus rules matter here. After the 5th team foul in a quarter, the opponent shoots free throws on every foul. Smart teams track foul counts and adjust their defense accordingly.',
            ],
            xpReward: 25,
            questions: [
              {
                id: 'bb-crunch-sit1',
                type: 'situational' as const,
                question: 'What should the trailing team do?',
                situationContext: '1:30 left | Down by 4 | Opponent has the ball',
                options: [
                  'Play tight defense and hope for a steal',
                  'Intentionally foul to stop the clock and get the ball back',
                  'Call timeout to rest the players',
                  'Switch to a zone defense',
                ],
                correctIndex: 1,
                explanation: 'Down 4 with 90 seconds left, you need possessions. Fouling stops the clock and gets you the ball back (after free throws). Playing straight-up defense lets the opponent drain the clock.',
              },
              {
                id: 'bb-crunch-sit2',
                type: 'situational' as const,
                question: 'What should the point guard do?',
                situationContext: 'Tied game | 24 seconds left | Your team has the ball',
                options: [
                  'Immediately shoot a three to take the lead',
                  'Hold the ball until the shot clock is low, then run the final play for the last shot',
                  'Pass it to the center to post up',
                  'Call a timeout — there\'s no rush',
                ],
                correctIndex: 1,
                explanation: 'In a tied game with the ball, you want the last shot. Hold until about 7-8 seconds on the clock, then run your best play. If you score, the opponent has no time to respond.',
              },
              {
                id: 'bb-crunch-match1',
                type: 'matching' as const,
                question: 'Match each late-game scenario with the correct strategy.',
                options: [],
                correctIndex: 0,
                matchingPairs: [
                  ['Up 3, opponent has ball, 10 sec left', 'Defend the three-point line, give up twos'],
                  ['Down 1, you have ball, 5 sec left', 'Drive to the basket for a high-percentage shot'],
                  ['Up 15 with 2 min left', 'Sub in bench players, run out the clock'],
                  ['Tied, opponent shooting free throws', 'Box out for the rebound on a miss'],
                ],
                explanation: 'Late-game strategy changes based on the score, time, and who has possession. Each scenario demands a different approach.',
              },
            ],
          },
          {
            id: 'bball-timeout-strategy',
            title: 'Timeout Management',
            description: 'Using stoppages to shift momentum.',
            conceptText: [
              'NBA teams get 7 timeouts per game — they\'re not just rest breaks. A well-timed timeout can stop an opponent\'s run, set up a crucial play, or advance the ball.',
              'After a timeout in the last 2 minutes, the team gets to inbound from halfcourt instead of under their own basket. This is huge for designing game-winning plays.',
              'Coaches save timeouts for the 4th quarter like currency. Using all 7 by the third quarter leaves you helpless in crunch time — you can\'t stop runs or draw up plays when it matters most.',
            ],
            xpReward: 20,
            questions: [
              {
                id: 'bb-to-q1',
                type: 'multiple-choice' as const,
                question: 'Why do coaches save timeouts for the 4th quarter?',
                options: [
                  'The rules require at least 2 timeouts in the 4th quarter',
                  'Players get more tired in the 4th quarter',
                  'To stop opponent runs, draw up plays, and manage the clock in crucial moments',
                  'Timeouts are worth more points in the 4th quarter',
                ],
                correctIndex: 2,
                explanation: 'Timeouts in the 4th quarter let you stop opponent momentum, advance the ball, and draw up specific plays for critical possessions. Running out early leaves you powerless.',
              },
              {
                id: 'bb-to-tf1',
                type: 'true-false' as const,
                question: 'After a timeout in the final two minutes, the offensive team can inbound from halfcourt.',
                options: [],
                correctIndex: 0,
                correctAnswer: true,
                explanation: 'True! In the NBA, a timeout in the last two minutes of the 4th quarter (or OT) lets the team advance the ball to the halfcourt line for the inbound. This is a key strategic advantage.',
              },
              {
                id: 'bb-to-sit1',
                type: 'situational' as const,
                question: 'Should the coach call timeout?',
                situationContext: 'Opponent on 12-0 run | Your team has 3 timeouts | 6 min left in 4th',
                options: [
                  'No, let the players fight through it',
                  'Yes, stop the momentum and reset the team\'s focus',
                  'No, save all timeouts for the final minute',
                  'Yes, but only to sub in bench players',
                ],
                correctIndex: 1,
                explanation: 'A 12-0 run is devastating momentum. A timeout breaks the opponent\'s rhythm, lets you adjust strategy, and gives your team a mental reset. With 3 timeouts and 6 minutes left, you can afford to use one.',
              },
            ],
          },
        ],
      },
    ],
  },
  // ── New: Transition Game (Beginner) ──────────────────────
  {
    id: 'basketball-transition',
    difficulty: 'beginner' as const,
    title: 'Transition Basketball',
    description: 'Fast breaks, outlet passes, and running the floor.',
    color: 'primary',
    units: [
      {
        id: 'bball-fast-break',
        title: 'The Fast Break',
        icon: '💨',
        requiredXp: 40,
        lessons: [
          {
            id: 'bball-transition-101',
            title: 'Fast Break Basics',
            description: 'Score before the defense can set up.',
            conceptText: [
              'A fast break starts the moment your team gets the ball — from a rebound, steal, or made basket. The idea is simple: push the ball up court before the defense can get set.',
              'The outlet pass is the key to a great fast break. After grabbing a rebound, the center immediately passes to the point guard on the wing, who pushes the ball up the sideline at full speed.',
              'The best fast breaks create advantages: 2-on-1, 3-on-2, or even 3-on-1. The ball handler reads the defense and makes the right decision — layup, pass to a teammate, or pull up for a jumper.',
            ],
            xpReward: 15,
            questions: [
              {
                id: 'bb-fb-q1',
                type: 'multiple-choice' as const,
                question: 'What starts a fast break?',
                options: [
                  'The coach calling a play',
                  'A change in possession — rebound, steal, or made basket',
                  'A timeout',
                  'The point guard crossing halfcourt',
                ],
                correctIndex: 1,
                explanation: 'Fast breaks begin with a change of possession. The quicker the transition from defense to offense, the more likely you\'ll catch the opponent before they can set their defense.',
              },
              {
                id: 'bb-fb-match1',
                type: 'matching' as const,
                question: 'Match each fast break role with the player who typically fills it.',
                options: [],
                correctIndex: 0,
                matchingPairs: [
                  ['Outlet passer', 'Center/Power Forward'],
                  ['Ball handler', 'Point Guard'],
                  ['Lane filler (left)', 'Small Forward/Shooting Guard'],
                  ['Trailer', 'Power Forward'],
                ],
                explanation: 'Each player has a specific role in the fast break. The big man rebounds and outlets, the PG handles, wings fill the lanes, and the trailer arrives late for open threes.',
              },
              {
                id: 'bb-fb-tf1',
                type: 'true-false' as const,
                question: 'In a 2-on-1 fast break, the ball handler should always pass to their teammate.',
                options: [],
                correctIndex: 0,
                correctAnswer: false,
                explanation: 'False! The ball handler should read the defender. If the defender commits to stopping the pass, take the layup. If they guard you, dish to the open teammate. Read and react!',
              },
            ],
          },
        ],
      },
    ],
  },
];

// ---- Helper functions ----

export function getBasketballTotalLessons(): number {
  let total = 0;
  for (const path of basketballLearningPaths) {
    for (const unit of path.units) {
      total += unit.lessons.length;
    }
  }
  return total;
}

export function findBasketballLesson(lessonId: string): { path: LearningPath; unit: Unit; lesson: Lesson } | null {
  for (const path of basketballLearningPaths) {
    for (const unit of path.units) {
      for (const lesson of unit.lessons) {
        if (lesson.id === lessonId) {
          return { path, unit, lesson };
        }
      }
    }
  }
  return null;
}

export function getNextBasketballLesson(currentLessonId: string, xp: number): Lesson | null {
  let found = false;
  for (const path of basketballLearningPaths) {
    for (const unit of path.units) {
      if (xp < unit.requiredXp) continue;
      for (const lesson of unit.lessons) {
        if (found) return lesson;
        if (lesson.id === currentLessonId) found = true;
      }
    }
  }
  return null;
}
